import apiClient from 'api/services/apiClient';
import { MESSAGE_COMMON } from 'constants/string/messages';
import { isApiResponseError, validateMessageResponse } from 'utils/helper';

/**
 * @name createSurvey
 * @description Create a new survey
 * @param {object} data
 * @response success - 200 OK
 * @response failure - 400 Bad Request
 * @returns {object} response.data
 */
export const createSurvey = async (data) => {
  const response = await apiClient.request('POST', '/admin/surveys', data);
  if (!isApiResponseError(response)) {
    return {
      ...response.data,
      ...validateMessageResponse(response.data.message, 'string', MESSAGE_COMMON.SUCCESS),
    };
  } else {
    throw {
      ...validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE),
      field_names: response?.error?.field_names || [],
    };
  }
};

/**
 * @name updateSurvey
 * @description Update a survey by id
 * @param {string | number} id
 * @param {object} data
 * @response success - 200 OK
 * @response failure - 400 Bad Request
 * @returns {object} response.data
 */
export const updateSurvey = async (id, data) => {
  const response = await apiClient.request('PUT', `/admin/surveys/${id}`, data);
  if (!isApiResponseError(response)) {
    return {
      ...response.data,
      ...validateMessageResponse(response.data.message, 'string', MESSAGE_COMMON.SUCCESS),
    };
  } else {
    throw {
      ...validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE),
      status_code: response.status,
      field_names: response?.error?.field_names || [],
    };
  }
};

/**
 * @name surveyDetail
 * @description Get the detail of a survey by id
 * @param {string | number} id
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const surveyDetail = async (id) => {
  const response = await apiClient.request('GET', `/admin/surveys/${id}`);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name surveys
 * @description Get the list of surveys
 * @param {string} query
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const surveys = async (query) => {
  const response = await apiClient.request('GET', `/admin/surveys${query}`);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name surveysDeleteById
 * @description Delete a survey by id
 * @param {string | number} id
 * @response success - 204 No Content
 * @response failure - 404 Not Found
 */
export const surveysDeleteById = async (id) => {
  const response = await apiClient.request('DELETE', `/admin/surveys/${id}`);
  if (!isApiResponseError(response)) {
    return true;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name surveysDeleteByIds
 * @description Delete multiple surveys by ids
 * @param {Array.<string | number>} ids
 * @response success - 204 No Content
 * @response failure - 404 Not Found
 */
export const surveysDeleteByIds = async (ids) => {
  const response = await apiClient.request('DELETE', '/admin/surveys', ids);
  if (!isApiResponseError(response)) {
    return true;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name surveyMakeDuplicate
 * @description Clone a survey by id
 * @param {string | number} id
 * @param {object} data
 * @response success - 200 OK
 * @response failure - 400 Bad Request
 * @returns {object} response.data
 */
export const surveyMakeDuplicate = async (id, data) => {
  const response = await apiClient.request('POST', `/admin/surveys/${id}/make-duplicate`, data);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name surveyMakePrivate
 * @description Make a survey from public to private
 * @param {string | number} id
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const surveyMakePrivate = async (id) => {
  const response = await apiClient.request('GET', `/admin/surveys/${id}/make-private`);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name surveyExport
 * @description Export a survey by id
 * @param {object} data
 * @param {string} data.id
 * @param {string} data.export_type
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {Blob} response.data
 */
export const surveyExport = async (data, export_type) => {
  const response = await apiClient.request('POST', `/admin/surveys/export?export_type=${export_type}`, data, {
    responseType: 'blob',
  });
  if (!isApiResponseError(response)) {
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'survey_export.zip';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch !== null && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    const blob = response?.data;
    const url = window.URL.createObjectURL(new Blob([blob], { type: blob.type }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name surveyPublicPeriodSetting
 * @description Set the public period of a survey
 * @param {object} data
 * @param {string} data.id
 * @param {string} data.export_type
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const surveyPublicPeriodSetting = async (data, id) => {
  const response = await apiClient.request('POST', `/admin/surveys/${id}/make-public-scheduled`, data);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name makePublicScheduled
 * @description Make a survey public scheduled by id
 * @param {string | number} id
 * @param {object} data
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const makePublicScheduled = async (id, data) => {
  const response = await apiClient.request('POST', `/admin/surveys/${id}/make-public-scheduled`, data);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name cancelPublicScheduled
 * @description Cancel the public period of a survey
 * @param {string | number} id
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const cancelPublicScheduled = async (id) => {
  const response = await apiClient.request('GET', `/admin/surveys/${id}/cancel-public-scheduled`);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};
